import dayjs, { Dayjs } from "dayjs";
import isBetween from "dayjs/plugin/isBetween.js";
import ClickhouseDateTimeGranularity = App.Enums.ClickhouseDateTimeGranularity;
import AnnotatedEventDO = App.Data.AnnotatedEventDO;

dayjs.extend(isBetween);

export interface AnnotationBubblesPluginOptions {
	scaleId: string;
	granularity?: ClickhouseDateTimeGranularity;
	annotations?: AnnotatedEventDO[];
}

interface DateParserFunc {
	(str: string): Dayjs;
}

function getBarInterval(
	date: Dayjs,
	granularity: ClickhouseDateTimeGranularity
): [Dayjs, Dayjs] {
	switch (granularity) {
		case "day":
			return [date.startOf("day"), date.endOf("day")];
		case "week":
			return [date.startOf("isoWeek"), date.endOf("isoWeek")];
		case "month":
			return [date.startOf("month"), date.endOf("month")];
		case "quarter":
			return [date.startOf("quarter"), date.endOf("quarter")];
		case "year":
			return [date.startOf("year"), date.endOf("year")];
		default:
			return [date, date];
	}
}

function drawAnnotationIcon(
	ctx: CanvasRenderingContext2D,
	x: number,
	y: number,
	offsetIndex: number = 0
) {
	const radius = 7;

	ctx.save();
	ctx.beginPath();
	ctx.fillStyle = "#FDFEFE";
	ctx.arc(x, y - 20, radius, 0, 2 * Math.PI);
	ctx.fill();

	ctx.lineWidth = 1;
	ctx.strokeStyle = "#424242";
	ctx.stroke();

	if (offsetIndex > 0) {
		ctx.fillStyle = "#424242";
		ctx.font = "10px sans-serif";
		ctx.textAlign = "center";
		ctx.textBaseline = "middle";
		ctx.fillText(`${offsetIndex + 1}`, x, y - 20);
	}

	ctx.restore();
}

function granularityToDateParser(
	granularity: ClickhouseDateTimeGranularity
): DateParserFunc | null {
	if (granularity.includes("min")) {
		return null;
	}

	if (granularity === "week") {
		return (str) => {
			const parts = str.split("-");
			return dayjs()
				.year(Number(parts[0]))
				.isoWeek(Number(parts[1]))
				.startOf("isoWeek");
		};
	}

	if (granularity === "month") {
		return (str) => {
			const parts = str.split("-");
			return dayjs()
				.startOf("month")
				.year(Number(parts[0]))
				.month(Number(parts[1]) - 1);
		};
	}

	if (granularity === "quarter") {
		return (str) => {
			const [year, q] = str.split("-Q");
			const quarter = Number(q);
			const startMonth = (quarter - 1) * 3;
			return dayjs(
				`${year}-${String(startMonth + 1).padStart(2, "0")}-01`
			).startOf("month");
		};
	}

	return (str) => dayjs(str);
}

// Caching interface
interface AnnotationCache {
	tickMatchMap: Map<number, AnnotatedEventDO[]>;
	lastGranularity?: string;
	lastAnnotationHash?: string;
}

export default {
	id: "annotationBubbles",
	afterDraw(chart, args, options: AnnotationBubblesPluginOptions) {
		const { scaleId, granularity, annotations } = options;

		if (!annotations || !granularity || granularity.includes("min")) return;

		const scale = chart.scales[scaleId];
		if (!scale) return;

		const ctx = chart.ctx;
		const cacheKey = `${granularity}-${annotations.length}`;

		// @ts-expect-error: attach custom cache
		chart.$annotationBubblesCache ??= {};
		// @ts-expect-error: annotate the type for cache
		const cache: AnnotationCache = chart.$annotationBubblesCache;

		if (
			cache.lastGranularity !== granularity ||
			cache.lastAnnotationHash !== cacheKey
		) {
			cache.lastGranularity = granularity;
			cache.lastAnnotationHash = cacheKey;
			cache.tickMatchMap = new Map();

			const parseTickDate = granularityToDateParser(granularity);
			if (!parseTickDate) return;

			for (const tick of scale.ticks) {
				const tickValue = scale.getLabelForValue(tick.value);
				const tickDate = parseTickDate(tickValue);
				if (!tickDate || !dayjs.isDayjs(tickDate)) continue;

				const [start, end] = getBarInterval(tickDate, granularity);

				const matchingAnnotations = annotations.filter((a) => {
					const from = dayjs(a.from);
					return from.isBetween(start, end, null, "[]");
				});

				cache.tickMatchMap.set(tick.value, matchingAnnotations);
			}
		}

		// Render icons
		for (const tick of scale.ticks) {
			const x = scale.getPixelForValue(tick.value);
			const y = chart.chartArea.bottom + 20;

			const annotationsForTick = cache.tickMatchMap.get(tick.value) ?? [];
			const shouldOffset = annotationsForTick.length > 1;

			annotationsForTick.forEach((annotation, index) => {
				const offset = shouldOffset ? index : 0;
				drawAnnotationIcon(ctx, x, y, offset);
			});
		}
	},
};
